import React from "react";

import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import ServerTopology from "../components/server/server_topology";

import { fetchSystemResources } from "../services/infraApi";
import { fetchPaperHealth } from "../services/paperApi";
import { requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";

/**
 * ServerPage — /server. (DAS-129 → 클라우드/로컬 구분 + 별칭 + 구조도 확장)
 *
 * 최상위 "Server" 네비게이션 페이지. go-server 가 self(Dashboard EC2) + agent(NUC·Mac mini)
 * 호스트 메트릭을 fan-out 집계해 단일 페이로드로 내려주고, 정적 호스트(Oracle·Strategy)는
 * 역할/가동 프로세스 맥락만 제공한다. 호스트는 클라우드/로컬로 분리해 렌더하며, 각 카드의
 * 별칭은 사용자가 직접 지정(localStorage 영속)할 수 있다. 상단 구조도는 서버 간 데이터 흐름을
 * 보여준다.
 *
 * 디자인: silver 토큰 기반 "정밀 텔레메트리 콘솔". docs/DESIGN_SYSTEM.md 토큰·접근성 준수.
 */
const POLL_INTERVAL_MS = 15_000;
const ALIAS_KEY = "server_aliases";

function loadAliases() {
    try {
        return JSON.parse(localStorage.getItem(ALIAS_KEY) || "{}") || {};
    } catch {
        return {};
    }
}
function persistAliases(map) {
    try {
        localStorage.setItem(ALIAS_KEY, JSON.stringify(map));
    } catch {
        /* storage 불가 환경 무시 */
    }
}
/** 별칭/React key 의 안정적 식별자 (백엔드 host → fallback name). */
function hostKey(host) {
    return host.host || host.name;
}

class ServerPageInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            loading: true,
            hosts: [],
            paperHealth: null,
            lastUpdated: null,
            aliases: loadAliases(),
            editingKey: null,
            draft: "",
        };
        this.shouldPoll = true;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.poll();
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.timer);
    }

    poll = () => {
        if (!this.shouldPoll) return;
        Promise.allSettled([fetchSystemResources(), fetchPaperHealth()])
            .then(([resRes, healthRes]) => {
                if (!this.shouldPoll) return;
                const data = resRes.status === "fulfilled" ? resRes.value.data : null;
                const paperHealth =
                    healthRes.status === "fulfilled" ? healthRes.value.data : null;
                this.setState({
                    hosts: Array.isArray(data?.hosts) ? data.hosts : [],
                    paperHealth,
                    loading: false,
                    lastUpdated: new Date(),
                });
            })
            .finally(() => {
                if (this.shouldPoll)
                    this.timer = setTimeout(this.poll, POLL_INTERVAL_MS);
            });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    // ── 별칭 편집 ──
    startEdit = (key, current) => this.setState({ editingKey: key, draft: current || "" });
    cancelEdit = () => this.setState({ editingKey: null, draft: "" });
    onDraftChange = (v) => this.setState({ draft: v });
    saveAlias = (key) => {
        const next = { ...this.state.aliases };
        const trimmed = this.state.draft.trim();
        if (trimmed) next[key] = trimmed;
        else delete next[key];
        persistAliases(next);
        this.setState({ aliases: next, editingKey: null, draft: "" });
    };
    clearAlias = (key) => {
        const next = { ...this.state.aliases };
        delete next[key];
        persistAliases(next);
        this.setState({ aliases: next, editingKey: null, draft: "" });
    };

    renderGroup(hosts) {
        const { paperHealth, aliases, editingKey, draft } = this.state;
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {hosts.map((host) => {
                    const key = hostKey(host);
                    return (
                        <HostCard
                            key={key}
                            host={host}
                            hkey={key}
                            paperHealth={paperHealth}
                            alias={aliases[key]}
                            editing={editingKey === key}
                            draft={editingKey === key ? draft : ""}
                            onStartEdit={this.startEdit}
                            onDraftChange={this.onDraftChange}
                            onSave={this.saveAlias}
                            onCancel={this.cancelEdit}
                            onClear={this.clearAlias}
                        />
                    );
                })}
            </div>
        );
    }

    render() {
        const { collapsed, loading, hosts, lastUpdated } = this.state;

        const cloud = hosts.filter((h) => h.category === "cloud");
        const local = hosts.filter((h) => h.category === "local");
        const other = hosts.filter((h) => h.category !== "cloud" && h.category !== "local");

        const idleCount = hosts.filter((h) => h.idle).length;
        const activeCount = hosts.filter(
            (h) => !h.idle && hostStatus(h) !== "offline"
        ).length;

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? "sm:pl-[88px] lg:pl-[96px]" : "sm:pl-[252px] lg:pl-[260px]"
                    } px-4 py-4 sm:pr-8 sm:py-6 lg:pr-10`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <header className="mb-5">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div className="min-w-0">
                                <h1 className="text-2xl font-bold text-content tracking-tight">
                                    서버 인프라 · 자원 현황
                                </h1>
                                <p className="text-sm text-content-secondary mt-1 max-w-2xl">
                                    클라우드/로컬 서버를 구분해 가동 상태·자원·역할을 한눈에. 카드의
                                    별칭(✎)은 직접 지정할 수 있고, 자원 에이전트가 60초마다 보고한다
                                    (EC2는 직접 측정).
                                </p>
                            </div>
                            {!loading && (
                                <div className="flex items-center gap-2 shrink-0">
                                    <SummaryChip label="전체" value={String(hosts.length)} />
                                    <SummaryChip label="가동" value={String(activeCount)} tone="gain" />
                                    <SummaryChip label="유휴" value={String(idleCount)} tone="muted" />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-content-muted mt-3">
                            <span
                                className="inline-block w-1.5 h-1.5 rounded-full bg-gain animate-pulse"
                                aria-hidden="true"
                            />
                            <span>
                                마지막 갱신 {lastUpdated ? lastUpdated.toLocaleTimeString() : "—"} · 15초 자동
                            </span>
                        </div>
                    </header>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <SkeletonCard key={i} />
                            ))}
                        </div>
                    ) : hosts.length === 0 ? (
                        <div className="bg-surface-card border border-surface-border rounded-lg p-8 text-center text-sm text-content-secondary">
                            호스트 정보를 불러오지 못했습니다.
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <ServerTopology />

                            {cloud.length > 0 && (
                                <section>
                                    <GroupHeader
                                        icon={<CloudIcon />}
                                        title="클라우드 서버"
                                        count={cloud.length}
                                        desc="AWS · Oracle Cloud"
                                    />
                                    {this.renderGroup(cloud)}
                                </section>
                            )}

                            {local.length > 0 && (
                                <section>
                                    <GroupHeader
                                        icon={<HomeIcon />}
                                        title="개인 로컬 서버"
                                        count={local.length}
                                        desc="On-prem · 자택 네트워크"
                                    />
                                    {this.renderGroup(local)}
                                </section>
                            )}

                            {other.length > 0 && (
                                <section>
                                    <GroupHeader icon={<ServerStackIcon />} title="기타" count={other.length} />
                                    {this.renderGroup(other)}
                                </section>
                            )}
                        </div>
                    )}

                    <ScrollToTopButton />
                </main>
            </div>
        );
    }
}

/** 호스트 상태 분류 → 액센트/배지 색상 결정의 단일 소스. */
function hostStatus(host) {
    if (host.idle) return "idle";
    if (host.metrics_source === "none") return "running"; // 가동 중(라이브 메트릭 미수집)
    if (!host.reachable || !host.metrics?.ok) return "offline";
    const m = host.metrics;
    const util = Math.max(
        m.cpu_percent ?? 0,
        m.mem_total_mb > 0 ? (m.mem_used_mb / m.mem_total_mb) * 100 : 0,
        m.disk_total_gb > 0 ? (m.disk_used_gb / m.disk_total_gb) * 100 : 0
    );
    if (util >= 90) return "crit";
    if (util >= 70) return "warn";
    return "ok";
}

const ACCENT = {
    ok: "border-l-gain",
    warn: "border-l-warning",
    crit: "border-l-loss",
    running: "border-l-brand",
    offline: "border-l-loss",
    idle: "border-l-surface-border-light",
};

function GroupHeader({ icon, title, count, desc }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <span className="text-content-secondary" aria-hidden="true">{icon}</span>
            <h2 className="text-sm font-semibold text-content">{title}</h2>
            <span className="text-xs font-mono text-content-muted bg-surface-card-alt border border-surface-border rounded px-1.5 py-0.5">
                {count}
            </span>
            {desc && <span className="text-xs text-content-muted">· {desc}</span>}
        </div>
    );
}

function HostCard({
    host, hkey, paperHealth, alias, editing, draft,
    onStartEdit, onDraftChange, onSave, onCancel, onClear,
}) {
    const status = hostStatus(host);
    const m = host.metrics;
    // 라이브 게이지는 메트릭 수집 여부로만 결정 — 유휴 서버도 에이전트가 있으면 게이지 표시.
    const hasLive = host.reachable && m && m.ok;
    const runsPaper = !!host.runs_paper_trader;
    const displayName = alias || host.name;

    return (
        <section
            className={`bg-surface-card border border-surface-border border-l-2 ${ACCENT[status]} rounded-lg p-4 transition-colors hover:bg-surface-hover`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    {editing ? (
                        <AliasEditor
                            hkey={hkey}
                            draft={draft}
                            placeholder={host.name}
                            onDraftChange={onDraftChange}
                            onSave={onSave}
                            onCancel={onCancel}
                            onClear={onClear}
                            hasAlias={!!alias}
                        />
                    ) : (
                        <div className="flex items-center gap-1.5 min-w-0">
                            <h3 className="text-sm font-semibold text-content truncate">{displayName}</h3>
                            <button
                                type="button"
                                onClick={() => onStartEdit(hkey, alias)}
                                className="shrink-0 text-content-muted hover:text-brand transition-colors p-0.5 -m-0.5"
                                aria-label="별칭 편집"
                                title="별칭 편집"
                            >
                                <PencilIcon />
                            </button>
                        </div>
                    )}
                    {alias && !editing && (
                        <div className="text-xs text-content-muted truncate mt-0.5">{host.name}</div>
                    )}
                    <div className="text-xs text-content-muted mt-0.5 font-mono truncate">{host.location}</div>
                </div>
                <StatusBadge status={status} />
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {host.provider && <ProviderChip provider={host.provider} />}
                {host.service && host.service !== "—" && (
                    <span className="text-xs font-mono text-content-secondary bg-surface-card-alt border border-surface-border rounded px-1.5 py-0.5 truncate max-w-full">
                        {host.service}
                    </span>
                )}
            </div>

            <div className="text-xs text-content-secondary mt-2">{host.role}</div>
            {host.reserved && (
                <div className="text-xs text-content-muted mt-0.5">
                    <span className="text-content-muted">예약 </span>
                    {host.reserved}
                </div>
            )}
            {host.strategies && host.strategies !== "—" && (
                <div className="text-xs text-content-secondary mt-0.5">
                    <span className="text-content-muted">전략 </span>
                    {host.strategies}
                </div>
            )}

            <div className="mt-3 pt-3 border-t border-surface-border">
                {hasLive ? (
                    <div className="space-y-2.5">
                        {host.idle && (
                            <div className="text-xs text-content-muted -mt-0.5 mb-0.5">
                                유휴 서버 · 실측 자원 (워크로드 없음)
                            </div>
                        )}
                        <ResGauge
                            label="CPU"
                            pct={m.cpu_percent}
                            detail={`유휴 ${(100 - (m.cpu_percent || 0)).toFixed(0)}%`}
                        />
                        <ResGauge
                            label="메모리"
                            pct={m.mem_total_mb > 0 ? (m.mem_used_mb / m.mem_total_mb) * 100 : 0}
                            detail={`${(m.mem_used_mb / 1024).toFixed(1)} / ${(m.mem_total_mb / 1024).toFixed(1)} GB · 여유 ${((m.mem_total_mb - m.mem_used_mb) / 1024).toFixed(1)}GB`}
                        />
                        <ResGauge
                            label="디스크"
                            pct={m.disk_total_gb > 0 ? (m.disk_used_gb / m.disk_total_gb) * 100 : 0}
                            detail={`${m.disk_used_gb.toFixed(0)} / ${m.disk_total_gb.toFixed(0)} GB · 여유 ${(m.disk_total_gb - m.disk_used_gb).toFixed(0)}GB`}
                        />
                        <div className="flex items-center justify-between pt-0.5">
                            <span className="text-xs text-content-muted">업타임</span>
                            <span className="text-xs text-content-secondary tabular-nums font-mono">
                                {fmtUptime(m.uptime_sec)}
                            </span>
                        </div>
                        {runsPaper && paperHealth && <ProcessStrip health={paperHealth} />}
                    </div>
                ) : status === "idle" ? (
                    <div className="flex items-center gap-2 text-xs text-content-muted py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-surface-border-light shrink-0" aria-hidden="true" />
                        유휴 서버 · 가동 중인 프로세스 없음
                    </div>
                ) : status === "running" ? (
                    <div className="flex items-center gap-2 text-xs text-content-muted py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" aria-hidden="true" />
                        가동 중 · 라이브 메트릭 미수집 (에이전트 미배치)
                    </div>
                ) : (
                    <div role="status" className="flex items-center gap-2 text-xs text-loss py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-loss shrink-0" aria-hidden="true" />
                        오프라인 · 메트릭 수집 실패
                    </div>
                )}
            </div>
        </section>
    );
}

/** 인라인 별칭 편집기. Enter 저장 · Esc 취소 · blur 저장. */
function AliasEditor({ hkey, draft, placeholder, onDraftChange, onSave, onCancel, onClear, hasAlias }) {
    return (
        <div className="flex items-center gap-1">
            <input
                type="text"
                autoFocus
                value={draft}
                maxLength={32}
                placeholder={placeholder}
                onChange={(e) => onDraftChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") onSave(hkey);
                    else if (e.key === "Escape") onCancel();
                }}
                onBlur={() => onSave(hkey)}
                className="min-w-0 flex-1 text-sm font-semibold bg-surface-elevated border border-brand rounded px-1.5 py-0.5 text-content focus:outline-none"
                aria-label="별칭 입력"
            />
            <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSave(hkey)}
                className="shrink-0 text-gain hover:text-gain-dark p-0.5"
                aria-label="저장"
                title="저장"
            >
                <CheckIcon />
            </button>
            {hasAlias && (
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onClear(hkey)}
                    className="shrink-0 text-content-muted hover:text-loss p-0.5"
                    aria-label="별칭 삭제"
                    title="기본 이름으로 되돌리기"
                >
                    <TrashIcon />
                </button>
            )}
        </div>
    );
}

/** paper-trader 프로세스 보조 메트릭 (NUC 카드 전용). */
function ProcessStrip({ health }) {
    return (
        <div className="mt-1 pt-2.5 border-t border-surface-border-light">
            <div className="text-xs text-content-muted mb-1.5">paper-trader 프로세스</div>
            <dl className="grid grid-cols-3 gap-2">
                <ProcStat label="RSS" value={`${(health.rss_mb ?? 0).toFixed(0)}MB`} />
                <ProcStat label="goroutine" value={String(health.goroutines ?? "—")} />
                <ProcStat label="mark 심볼" value={String(health.mark_symbols ?? "—")} />
            </dl>
        </div>
    );
}

function ProcStat({ label, value }) {
    return (
        <div className="bg-surface-card-alt rounded px-2 py-1.5 min-w-0">
            <dt className="text-xs text-content-muted truncate">{label}</dt>
            <dd className="text-content tabular-nums font-mono text-xs truncate">{value}</dd>
        </div>
    );
}

/** 자원 게이지 — 라벨 + 큰 % 수치 + 임계 색상 바 + 절대값 캡션. */
function ResGauge({ label, pct, detail }) {
    const clamped = Math.max(0, Math.min(100, pct || 0));
    const tone = clamped >= 90 ? "loss" : clamped >= 70 ? "warning" : "brand";
    const fillClass = { loss: "bg-loss", warning: "bg-warning", brand: "bg-brand" }[tone];
    const numClass = { loss: "text-loss", warning: "text-warning", brand: "text-content" }[tone];
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs text-content-secondary">{label}</span>
                <span className={`text-sm font-mono font-semibold tabular-nums ${numClass}`}>
                    {clamped.toFixed(0)}
                    <span className="text-xs text-content-muted font-normal">%</span>
                </span>
            </div>
            <div
                className="h-1.5 bg-surface-elevated rounded-full overflow-hidden"
                role="meter"
                aria-label={`${label} 사용률`}
                aria-valuenow={Math.round(clamped)}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div
                    className={`h-full rounded-full ${fillClass} transition-[width] duration-500`}
                    style={{ width: `${clamped}%` }}
                />
            </div>
            {detail && (
                <div className="text-xs text-content-muted mt-1 tabular-nums font-mono">{detail}</div>
            )}
        </div>
    );
}

/** 상태 배지 — 가동/유휴/오프라인을 색상+라벨로. */
function StatusBadge({ status }) {
    if (status === "idle") {
        return (
            <span className="text-xs px-2 py-0.5 rounded shrink-0 bg-surface-elevated text-content-muted">유휴</span>
        );
    }
    if (status === "offline") {
        return (
            <span className="text-xs px-2 py-0.5 rounded shrink-0 bg-loss-subtle text-loss">오프라인</span>
        );
    }
    const live = status === "ok" || status === "warn" || status === "crit";
    return (
        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded shrink-0 bg-gain-subtle text-gain">
            {live && <span className="w-1.5 h-1.5 rounded-full bg-gain animate-pulse" aria-hidden="true" />}
            가동 중
        </span>
    );
}

function ProviderChip({ provider }) {
    const map = {
        AWS: "bg-warning-subtle text-warning-text",
        "Oracle Cloud": "bg-loss-subtle text-loss",
        "On-prem": "bg-brand-subtle text-brand",
    };
    const cls = map[provider] || "bg-surface-elevated text-content-muted";
    return <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${cls}`}>{provider}</span>;
}

function SummaryChip({ label, value, tone = "neutral" }) {
    const valClass =
        tone === "loss" ? "text-loss"
            : tone === "warning" ? "text-warning"
            : tone === "gain" ? "text-gain"
            : tone === "muted" ? "text-content-muted"
            : "text-content";
    return (
        <div className="bg-surface-card-alt border border-surface-border rounded-md px-2.5 py-1.5 text-center min-w-14">
            <div className="text-xs text-content-muted leading-none">{label}</div>
            <div className={`text-sm font-mono font-semibold tabular-nums mt-1 leading-none ${valClass}`}>
                {value}
            </div>
        </div>
    );
}

function SkeletonCard() {
    return (
        <section className="bg-surface-card border border-surface-border border-l-2 border-l-surface-border-light rounded-lg p-4 animate-pulse">
            <div className="h-4 w-32 bg-surface-elevated rounded" />
            <div className="h-3 w-24 bg-surface-elevated rounded mt-2" />
            <div className="mt-3 pt-3 border-t border-surface-border space-y-3">
                <div className="h-1.5 w-full bg-surface-elevated rounded-full" />
                <div className="h-1.5 w-full bg-surface-elevated rounded-full" />
                <div className="h-1.5 w-full bg-surface-elevated rounded-full" />
            </div>
        </section>
    );
}

function fmtUptime(sec) {
    if (sec == null || sec <= 0) return "—";
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

// ── 아이콘 (16px stroke) ──
const Svg = (p) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p} />
);
const CloudIcon = () => (
    <Svg width="16" height="16"><path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.6 1.5A3.5 3.5 0 0 0 6.5 19z" /></Svg>
);
const HomeIcon = () => (
    <Svg width="16" height="16"><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></Svg>
);
const ServerStackIcon = () => (
    <Svg width="16" height="16"><rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /><path d="M7 7h.01M7 17h.01" /></Svg>
);
const PencilIcon = () => (
    <Svg><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></Svg>
);
const CheckIcon = () => (
    <Svg><path d="M20 6 9 17l-5-5" /></Svg>
);
const TrashIcon = () => (
    <Svg><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6v14h12V6" /></Svg>
);

export default function ServerPage() {
    return <ServerPageInner />;
}
